import { Button } from '@features/ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ProductAccountTypes,
  ProductTypes,
  TOnboardingAccount,
  TPackages,
} from '@src/types';
import { accountTypes } from '@pages/user/checkout/checkoutData.ts';
import { getProductsAccountsOnboarding } from '@src/services/products.ts';
import { AddonsCard } from '@pages/user/checkout/AddonsCard.tsx';
import { FeatureCard } from '@pages/user/checkout/FeatureCard.tsx';
import { SelectAccountCard } from '@pages/user/checkout/SelectAccountCard.tsx';
import { useRenewStore } from '@stores/renewStore.ts';

export const RenewSelectAccountPage = () => {
  const { t } = useTranslation();
  const [selectedAccount, setSelectedAccount] = useState<TPackages>();
  const [packages, setPackages] = useState<TPackages[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<number[]>([]);

  const navigate = useNavigate();
  const { platform, account, setAccount } = useRenewStore();

  const fetchProducts = useCallback(async () => {
    if (!platform) return;
    const res = await getProductsAccountsOnboarding({
      categories: ProductTypes.TRADING_ACCOUNT,
      account_status: 'active',
      datafeed: platform,
      account_types: [
        ProductAccountTypes.WEEKLY,
        ProductAccountTypes.ENTRY,
        ProductAccountTypes.FOUNDATION,
        ProductAccountTypes.BUNDLE,
      ],
    });
    const merged = res.data;
    const uniqueById = Array.from(
      new Map(merged.map((item) => [item.id, item])).values()
    );

    const packagesSort = uniqueById?.sort((a, b) => {
      const aProductId = Number(a?.id);
      const bProductId = Number(b?.id);

      if (aProductId > bProductId) {
        return 1;
      }
      if (aProductId < bProductId) {
        return -1;
      }

      return 0;
    });

    if (!packagesSort?.length) return;
    setPackages(packagesSort);
    if (!account) {
      setSelectedAccount(packagesSort[0]);

      return;
    }
    setSelectedAccount(account);
    setSelectedAddons(account.selectedAddonIds ?? []);
  }, [account, platform]);

  useEffect(() => {
    fetchProducts().then();
  }, [fetchProducts]);

  const handleSelectedAccountType = useCallback(
    (type: ProductAccountTypes) => {
      const findPackage = packages.find((item) => item.type === type);

      if (findPackage) {
        setSelectedAccount(findPackage);

        return;
      }
      setSelectedAccount({ type });
    },
    [packages]
  );

  const handleProceed = useCallback(() => {
    const payload: TOnboardingAccount = {
      ...selectedAccount,
      selectedAddonIds: selectedAddons,
    };

    setAccount(payload);
    navigate('/renew/review-checkout');
  }, [navigate, selectedAccount, selectedAddons, setAccount]);

  const handleSelectAccount = useCallback((account: TPackages) => {
    setSelectedAccount(account);
    setSelectedAddons([]);
  }, []);

  return (
    <div className="mt-0 lg:mt-10 pb-10">
      <h3 className="text-[25px] mb-6 font-medium">
        1. {t('selectAccountPage.title')}
      </h3>
      <div className="flex gap-4 flex-wrap mb-6">
        {accountTypes.map((accountType) => (
          <Button
            key={accountType.value}
            className="text-xl lg:text-2xl h-14 font-bold min-w-[142px] lg:min-w-[195px] rounded-[10px] border-white"
            variant={
              selectedAccount?.type === accountType.value
                ? 'gradient'
                : 'outline'
            }
            onClick={() => handleSelectedAccountType(accountType.value)}
          >
            {accountType.label}
          </Button>
        ))}
      </div>
      <div className="flex mb-6 gap-5 lg:gap-11 flex-wrap lg:flex-nowrap">
        <SelectAccountCard
          packages={
            packages.filter((item) => item.type === selectedAccount?.type) ?? []
          }
          selectedAccount={selectedAccount}
          onSelectAccount={handleSelectAccount}
        />
        <FeatureCard features={selectedAccount} />
        <AddonsCard
          selectedAddons={selectedAddons}
          selectedPackage={selectedAccount}
          onSelectedAddons={setSelectedAddons}
        />
      </div>
      <div className="flex items-center justify-end gap-5">
        <Button
          className="lg:min-w-[145px]"
          disabled={!selectedAccount?.id}
          size={'lg'}
          variant={'gradient'}
          onClick={handleProceed}
        >
          {t('common.form.next')}
        </Button>
      </div>
    </div>
  );
};
